import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Lock, User, Eye, EyeOff, AlertCircle, ExternalLink, GraduationCap, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { GENIUS_LOCI_FINANCING_TEXT, ONERI_PROGRAMS } from '../constants/oneri';
import { submitCreatorRegistration } from '../services/creatorRegistrationService';
import { LanguageSelector } from '../components/LanguageSelector';

const Login = () => {
  const { t } = useTranslation();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showCreatorForm, setShowCreatorForm] = useState(false);
  const [creatorLoading, setCreatorLoading] = useState(false);
  const [creatorSuccess, setCreatorSuccess] = useState('');
  const [creatorError, setCreatorError] = useState('');
  const [selectedOneriId, setSelectedOneriId] = useState<string>(ONERI_PROGRAMS[0]?.id || '');
  const [creatorForm, setCreatorForm] = useState({
    fullName: '',
    email: '',
    username: '',
    organization: '',
    notes: '',
  });
  const { login } = useAuth();
  const navigate = useNavigate();
  const selectedOneri = useMemo(
    () => ONERI_PROGRAMS.find((program) => program.id === selectedOneriId) || ONERI_PROGRAMS[0],
    [selectedOneriId],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(username, password);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError('Accesso non riuscito. Verifica username/password legacy.');
      console.error('Legacy login error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreatorRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreatorError('');
    setCreatorSuccess('');
    setCreatorLoading(true);
    try {
      const payload = {
        fullName: creatorForm.fullName.trim(),
        email: creatorForm.email.trim(),
        username: creatorForm.username.trim(),
        organization: creatorForm.organization.trim(),
        notes: creatorForm.notes.trim(),
        requestType: 'edu' as const,
      };
      await submitCreatorRegistration(payload);
      setCreatorSuccess('Richiesta EDU inviata. Il superadmin valuterà l’attivazione per scuole e università.');
      setCreatorForm({
        fullName: '',
        email: '',
        username: '',
        organization: '',
        notes: '',
      });
      setTimeout(() => setShowCreatorForm(false), 1500);
    } catch (err) {
      console.error(err);
      setCreatorError('Invio non riuscito. Riprova tra poco.');
    } finally {
      setCreatorLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 via-accent/5 to-tertiary/15 flex flex-col items-center justify-center p-4 gap-8">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <img src="/glooci_logo.png" alt="Glooci" className="h-16 w-auto mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-text-primary mb-2">{t('login.title')}</h1>
          <p className="text-text-secondary mb-4">{t('login.subtitle')}</p>
          <div className="flex justify-center">
            <LanguageSelector />
          </div>
        </div>


        <div className="card p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">Username</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" size={20} />
                <input
                  type="text"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                  placeholder="username"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" size={20} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-center space-x-2 text-error bg-error/10 p-3 rounded-lg">
                <AlertCircle size={20} />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full btn-primary py-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Accesso in corso...' : 'Accedi'}
            </button>
          </form>

          <div className="mt-5 pt-5 border-t border-border">
            <button
              type="button"
              onClick={() => {
                setShowCreatorForm((prev) => !prev);
                setCreatorError('');
                setCreatorSuccess('');
              }}
              className="w-full inline-flex items-center justify-center gap-2 text-sm border border-primary/30 rounded-lg px-4 py-2.5 text-primary hover:bg-primary/5 transition-colors"
            >
              <GraduationCap size={15} />
              Richiesta EDU per scuole e università
            </button>
          </div>
        </div>
      </div>

      {showCreatorForm && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm p-4 flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <h2 className="text-lg font-semibold text-text-primary">Richiesta EDU</h2>
              <button
                type="button"
                onClick={() => setShowCreatorForm(false)}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-text-secondary"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleCreatorRequest} className="p-6 space-y-4">
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-text-secondary">
                Questo flusso è riservato a scuole, università e progetti educational che richiedono valutazione del superadmin.
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="text-sm block">
                  <span className="block mb-1 text-text-secondary">Nome e cognome</span>
                  <input
                    required
                    className="w-full rounded-lg border border-border px-3 py-2"
                    value={creatorForm.fullName}
                    onChange={(e) => setCreatorForm((prev) => ({ ...prev, fullName: e.target.value }))}
                  />
                </label>
                <label className="text-sm block">
                  <span className="block mb-1 text-text-secondary">Username desiderato</span>
                  <input
                    required
                    className="w-full rounded-lg border border-border px-3 py-2"
                    value={creatorForm.username}
                    onChange={(e) => setCreatorForm((prev) => ({ ...prev, username: e.target.value }))}
                  />
                </label>
              </div>

              <label className="text-sm block">
                <span className="block mb-1 text-text-secondary">Email</span>
                <input
                  required
                  type="email"
                  className="w-full rounded-lg border border-border px-3 py-2"
                  value={creatorForm.email}
                  onChange={(e) => setCreatorForm((prev) => ({ ...prev, email: e.target.value }))}
                />
              </label>

              <label className="text-sm block">
                <span className="block mb-1 text-text-secondary">Organizzazione</span>
                <input
                  className="w-full rounded-lg border border-border px-3 py-2"
                  value={creatorForm.organization}
                  onChange={(e) => setCreatorForm((prev) => ({ ...prev, organization: e.target.value }))}
                />
              </label>

              <label className="text-sm block">
                <span className="block mb-1 text-text-secondary">Motivazione educativa</span>
                <textarea
                  required
                  className="w-full rounded-lg border border-border px-3 py-2 min-h-[110px]"
                  value={creatorForm.notes}
                  onChange={(e) => setCreatorForm((prev) => ({ ...prev, notes: e.target.value }))}
                />
              </label>

              {creatorError && (
                <div className="rounded-lg border border-error/30 bg-error/10 p-3 text-sm text-error">{creatorError}</div>
              )}
              {creatorSuccess && (
                <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700">{creatorSuccess}</div>
              )}

              <div className="flex items-center justify-end gap-3 pt-1">
                <button type="button" className="btn-outline" onClick={() => setShowCreatorForm(false)}>
                  Annulla
                </button>
                <button type="submit" className="btn-primary" disabled={creatorLoading}>
                  {creatorLoading ? 'Invio...' : 'Invia richiesta EDU'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="max-w-2xl w-full">
        <div className="bg-white/80 backdrop-blur rounded-xl border border-border p-5">
          <p className="text-xs font-semibold text-text-primary mb-3">Oneri Informativi e Pubblicitari</p>
          <div className="flex flex-wrap gap-2 mb-3">
            {ONERI_PROGRAMS.map((program) => (
              <button
                key={program.id}
                type="button"
                onClick={() => setSelectedOneriId(program.id)}
                className={`text-xs px-2.5 py-0.5 rounded-full font-medium transition-colors ${
                  selectedOneri?.id === program.id
                    ? 'bg-primary text-white'
                    : 'bg-primary/15 text-primary hover:bg-primary/25'
                }`}
              >
                {program.sigla}
              </button>
            ))}
            <span className="text-xs bg-accent/20 text-secondary px-2.5 py-0.5 rounded-full font-medium">
              CulturaCrea
            </span>
          </div>
          {selectedOneri && (
            <div className="mb-3 rounded-lg border border-border bg-background/40 p-3">
              <p className="text-xs font-semibold text-text-primary">{selectedOneri.nome}</p>
              <p className="text-xs text-text-secondary leading-relaxed mt-1">{selectedOneri.descrizione}</p>
              <p className="text-xs text-text-secondary leading-relaxed mt-2">{selectedOneri.finanziamento}</p>
              <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
                <a
                  href={selectedOneri.projectUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                >
                  Scheda progetto <ExternalLink size={10} />
                </a>
                {selectedOneri.references.map((reference) => (
                  <a
                    key={`${selectedOneri.id}-${reference.url}`}
                    href={reference.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                  >
                    {reference.label} <ExternalLink size={10} />
                  </a>
                ))}
              </div>
            </div>
          )}
          <p className="text-xs text-text-secondary leading-relaxed">
            {GENIUS_LOCI_FINANCING_TEXT}
          </p>
          <div className="flex items-center gap-4 mt-3">
            <a href="https://www.glooci.it" target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1">
              www.glooci.it <ExternalLink size={10} />
            </a>
            <a href="https://www.glooci.it/privacy-policy/" target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1">
              Privacy Policy <ExternalLink size={10} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
